import type {ReactElement} from 'react';
import React from 'react';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {addDuplicatedPage, addNewPage} from '@Components/poster-editor/library/poster-editor-page-library';
import {getNumberOfPages} from '@Components/poster-editor/poster-editor-reducer';
import {useAppSelector} from '@/hooks';
import {onDeletePage, onMoveDownPage, onMoveUpPage} from '@Components/poster-editor/library/poster-editor-library';
import styles from './page-top-bar.module.scss';

interface PageTopBarProps {
  pageId: string;
}

export function PageTopBar({...props}: PageTopBarProps): ReactElement<any> | null {
  const pagesLength = useAppSelector(getNumberOfPages);
  const pageData = useAppSelector((state) => {
    return state.posterEditor.posterObject?.pages?.pagesHashMap[props.pageId];
  });
  const pageOrder = useAppSelector((state) => {
    const pageIndex = state.posterEditor.posterObject?.pages?.pageOrder.indexOf(props.pageId);
    return pageIndex !== -1 ? pageIndex : undefined;
  });
  const isMultiPageEnabled = useAppSelector((state) => {
    return state.userPreferences.isMultiPageEnabled;
  });

  const getPageEditingOptions = (): ReactElement<any> => {
    return (
      <>
        <Icon icon="icon-new-page" size={IconSize.SIZE_ICON_16} type={IconType.GHOST_UNCOLORED} shape={IconShape.SQUARE} onClick={onAddPage} />
        <Icon icon="icon-duplicate" size={IconSize.SIZE_ICON_16} type={IconType.GHOST_UNCOLORED} shape={IconShape.SQUARE} onClick={onDuplicatePage} />
        {pageOrder === 0 ? null : (
          <Icon
            icon="icon-arrow-top"
            isDisabled={pageOrder === 0}
            size={IconSize.SIZE_ICON_16}
            type={IconType.GHOST_UNCOLORED}
            shape={IconShape.SQUARE}
            onClick={(): void => {
              onMoveUpPage(props.pageId);
            }}
          />
        )}
        {pageOrder === pagesLength - 1 ? null : (
          <Icon
            icon="icon-arrow-bottom"
            isDisabled={pageOrder === pagesLength - 1}
            size={IconSize.SIZE_ICON_16}
            type={IconType.GHOST_UNCOLORED}
            shape={IconShape.SQUARE}
            onClick={(): void => {
              onMoveDownPage(props.pageId);
            }}
          />
        )}
        <Icon
          icon="icon-delete"
          className="color-danger-100"
          isDisabled={pagesLength === 1}
          size={IconSize.SIZE_ICON_16}
          type={IconType.GHOST_UNCOLORED}
          shape={IconShape.SQUARE}
          onClick={(): void => {
            onDeletePage(props.pageId);
          }}
        />
      </>
    );
  };

  const onDuplicatePage = (): void => {
    const currentPage = window.posterEditor?.whiteboard?.getCurrentPage();
    if (!currentPage) {
      return;
    }

    addDuplicatedPage(currentPage).catch((e) => {
      console.error(e);
    });
  };

  const onAddPage = (): void => {
    if (pageOrder !== undefined) {
      addNewPage(pageOrder + 1).catch((e) => {
        console.error(e);
      });
    }
  };

  if (!pageData || pageOrder === undefined || !isMultiPageEnabled) {
    return null;
  }

  return <div className={`flex-v-row spacing-p-l-1 ${styles.container}`}>{getPageEditingOptions()}</div>;
}
